import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Preloader from '../components/Preloader';
import Footer from '../components/Footer';
import { FaSearch } from 'react-icons/fa';
import ScrollReveal from '../components/ScrollReveal';

const tours = [
    {
        id: 1,
        title: 'Osu Castle',
        region: 'Greater Accra',
        description: 'A 17th-century castle in Osu, Accra, formerly the seat of government.',
        image: '/assets/osucastle.jpg',
        fullContent: 'Osu Castle, also known as Fort Christiansborg, is located in Osu, Accra. Built by the Danes in the 1660s, it has changed hands between Denmark, Portugal, the Akwamu, Britain, and finally Ghana. It served as the seat of government for most of Ghana\'s history.',
        yearBuilt: '1660s',
        images: ['/assets/osucastle.jpg', '/assets/osucastle.jpg', '/assets/osucastle.jpg'],
    },
    {
        id: 2,
        title: 'Castle of Good Hope',
        region: 'Western Cape',
        description: 'The oldest existing colonial building in South Africa, a bastion fort in Cape Town.',
        image: '/assets/goodhope.jpg',
        fullContent: 'The Castle of Good Hope is a bastion fort built in the 17th century in Cape Town. It was built by the Dutch East India Company between 1666 and 1679. It served as a replenishment station for ships sailing to the Dutch East Indies.',
        yearBuilt: '1666',
        images: ['/assets/castleofgoodhope1.jpg', '/assets/castleofgoodhope2.jpg', '/assets/castleofgoodhope3.jpg'],
    },
    {
        id: 3,
        title: 'National Museum of Ethiopia',
        region: 'Addis Ababa',
        description: 'Home to famous archaeological finds like Lucy, preserving Ethiopia\'s rich history.',
        image: '/assets/nationalethiopia.jpg',
        fullContent: 'The National Museum of Ethiopia houses the nation\'s artistic treasures as well as many of the most precious archaeological finds such as the fossilized remains of early hominids, the most famous being "Lucy".',
        yearBuilt: '1944',
        images: ['/assets/nationalethiopia.jpg', '/assets/nationalethiopia.jpg', '/assets/nationalethiopia.jpg'],
    },
];

const HistoricalTours = () => {
    const [filter, setFilter] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    const filteredTours = tours.filter(tour => {
        const matchesRegion = filter === 'All' || tour.region === filter;
        const matchesSearch = tour.title.toLowerCase().includes(searchTerm.toLowerCase()) || tour.description.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesRegion && matchesSearch;
    });

    const regions = ['All', ...new Set(tours.map(t => t.region))];

    const handleExplore = (tourId) => {
        navigate(`/historical-tour-detail/${tourId}`);
    };

    return (
        <div className='font-nixie-one bg-gray-50 min-h-screen text-gray-800'>
            <PageHeader />
            <Preloader />
            <main>
                {/* Hero Section */}
                <section className="relative h-[50vh] bg-cover bg-center" style={{ backgroundImage: "url('/assets/historical2.jpg')" }}>
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <div className="text-center text-white px-4">
                            <h1 className="text-5xl md:text-6xl font-cormorant-upright font-bold">Historical Tours</h1>
                            <p className="mt-4 text-sm md:text-sm max-w-3xl mx-auto">Journey through Africa's colonial past and explore the castles and forts across Ghana, South Africa, and Ethiopia that shaped our history.</p>
                        </div>
                    </div>
                </section>

                {/* Tours Section */}
                <section className="py-20 px-4">
                    <div className="container mx-auto">
                        <div className="flex flex-col md:flex-row justify-between items-center mb-12">
                            <div className="flex space-x-2 md:space-x-4 mb-6 md:mb-0 flex-wrap">
                                {regions.map(region => (
                                    <button key={region} onClick={() => setFilter(region)} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-300 ${filter === region ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                                        {region}
                                    </button>
                                ))}
                            </div>
                            <div className="relative w-96 md:w-96">
                                <input type="text" placeholder="Search historical sites..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-1 text-sm border rounded-full focus:outline-none focus:ring-2 focus:ring-green-500" />
                                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>

                        <ScrollReveal animationClass="animate-slide-left" threshold={0.1} delay={200}>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {filteredTours.map((tour) => (
                                    <div key={tour.id} className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col group">
                                        <div className="relative h-40">
                                            <img src={tour.image} alt={tour.title} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" />
                                            <span className="absolute top-2 right-2 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded">{tour.yearBuilt}</span>
                                        </div>
                                        <div className="p-6 flex flex-col flex-grow">
                                            <h3 className="text-xl font-bold font-cormorant-upright mb-2">{tour.title}</h3>
                                            <p className="text-gray-500 text-sm mb-2">{tour.region}</p>
                                            <p className="text-gray-600 text-sm flex-grow">{tour.description}</p>
                                            <button onClick={() => handleExplore(tour.id)} className="inline-block mt-4 self-start px-5 py-1 text-sm bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors duration-300">
                                                Explore
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </ScrollReveal>
                        {filteredTours.length === 0 && (
                            <p className="text-center text-gray-500 mt-8">No historical sites found matching your criteria.</p>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default HistoricalTours;
